import { appConfirm } from '../ui/appDialogStore'
import { Button as UiButton } from '../ui/Button'
import { ArrowLeft, LayoutList, Palette, Printer, RefreshCw, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import type { CatalogStartTab, TenantContext, ThemeDefinition } from '../../types'
import { PrintAgentSettings } from '../../features/local-printing'
import { AppModal, Button, Metric } from '../ui'
import { Select } from '../ui/Select'
import { clearLocalCache } from '../../lib/clearLocalCache'

type ConfigModalProps = {
  catalogStartTab: CatalogStartTab
  context: TenantContext
  lastSyncError: string | null
  onCatalogStartTabChange: (startTab: CatalogStartTab) => void
  onClose: () => void
  onRetrySync: () => void
  onThemeChange: (themeId: string) => void
  pendingCount: number
  themeId: string
  themes: ThemeDefinition[]
}

export function ConfigModal({
  catalogStartTab,
  context,
  lastSyncError,
  onCatalogStartTabChange,
  onClose,
  onRetrySync,
  onThemeChange,
  pendingCount,
  themeId,
  themes,
}: ConfigModalProps) {
  const [section, setSection] = useState<'general' | 'printing'>('general')
  const [clearingCache, setClearingCache] = useState(false)
  const [clearCacheError, setClearCacheError] = useState<string | null>(null)

  async function handleClearCache() {
    if (clearingCache || !(await appConfirm('¿Borrar todos los datos locales de este TPV? Se eliminarán la sesión, las preferencias y los datos pendientes de sincronizar. Solo se conservarán la URL del backend de impresión y su token. La aplicación se recargará; necesitarás conexión para volver a entrar.'))) return
    setClearingCache(true)
    setClearCacheError(null)
    try {
      await clearLocalCache()
    } catch {
      setClearCacheError('No se ha podido completar el borrado. La URL y el token de impresión se han conservado. Vuelve a intentarlo.')
      setClearingCache(false)
    }
  }
  const theme = themes.find((item) => item.id === themeId)
  const canManageHardware = context.canManageCash === true || ['manager', 'owner', 'superadmin'].includes(context.role)

  if (section === 'printing') {
    return (
      <AppModal containerClassName="!p-0 sm:!p-4" maxWidth={1024} label="Configuración de impresión" onClose={onClose} placement="bottom">
        <section className="flex max-h-[100svh] w-full flex-col bg-[var(--surface)] text-[var(--foreground)] shadow-[var(--shadow)] sm:max-h-[94svh] sm:rounded-[var(--radius)] sm:border sm:border-[var(--separator)]">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--separator)] p-4">
            <Button onClick={() => setSection('general')} size="sm" type="button" variant="tertiary"><ArrowLeft className="h-4 w-4" />Ajustes</Button>
            <Button disabled={clearingCache} className="!size-11 !min-h-11 !min-w-11 !shrink-0 !rounded-[12px] !p-0" aria-label="Cerrar" onClick={onClose} size="sm" type="button" variant="tertiary"><X className="h-4 w-4" /></Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] p-4 sm:p-5"><PrintAgentSettings canConfigure={canManageHardware} canOpenDrawer={canManageHardware} /></div>
        </section>
      </AppModal>
    )
  }

  return (
    <AppModal dismissDisabled={clearingCache} label="Configuración" maxWidth={880} onClose={onClose}>
      <section className="max-h-[calc(100dvh-24px)] w-full overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--surface)] p-5 text-[var(--foreground)] shadow-[var(--shadow)] sm:max-h-[calc(100dvh-48px)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Configuración</h2>
            <p className="text-sm text-[var(--muted)]">Contexto local de este TPV.</p>
          </div>
          <Button disabled={clearingCache} className="!size-11 !min-h-11 !min-w-11 !shrink-0 !rounded-[12px] !p-0" aria-label="Cerrar" onClick={onClose} size="sm" type="button" variant="tertiary">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 shadow-none">
          <Metric label="Negocio" value={context.tenantName} />
          <Metric label="Local" value={context.venueName} />
          <Metric label="Dispositivo" value={context.deviceName} />
          <Metric label="Usuario" value={context.userName} />
          <Metric label="Pendiente sync" value={String(pendingCount)} tone={pendingCount ? 'danger' : 'success'} />
        </div>

        <button
          onClick={() => {
            throw new Error('This is a test error for Sentry integration')
          }}
        >
          Break the world
        </button>

        <UiButton disabled={clearingCache} className="mt-5 flex min-h-14 w-full items-center justify-between rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--background)] px-4 text-left transition hover:border-[var(--accent)]" onClick={() => setSection('printing')} type="button">
          <span className="flex items-center gap-3"><Printer className="h-5 w-5 text-[var(--accent)]" /><span><strong className="block">Hardware · Impresión</strong><small className="text-[var(--muted)]">Agente local, impresoras, cajón y diagnóstico</small></span></span>
          <span aria-hidden="true">›</span>
        </UiButton>

        {lastSyncError ? (
          <div className="mt-4 rounded-[var(--radius)] border border-red-400/45 bg-red-500/10 p-3 text-sm">
            <p className="font-bold text-red-600 dark:text-red-300">Error de sincronización</p>
            <p className="mt-1 break-words text-[var(--foreground)]">{lastSyncError}</p>
            <Button className="mt-3" onClick={onRetrySync} size="sm" type="button" variant="secondary">
              <RefreshCw className="h-4 w-4" />
              Reintentar ahora
            </Button>
          </div>
        ) : null}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="mt-5 block">
            <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--muted)]">
              <Palette className="h-4 w-4" />
              Tema
            </span>
            <Select
              ariaLabel="Tema"
              onChange={onThemeChange}
              options={themes.map((item) => ({ label: item.name, value: item.id }))}
              value={theme?.id ?? themeId}
            />
          </div>

          <div className="mt-5 block">
            <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--muted)]">
              <LayoutList className="h-4 w-4" />
              Primera pestaña del catálogo
            </span>
            <Select
              ariaLabel="Primera pestaña del catálogo"
              onChange={(value) => onCatalogStartTabChange(value as CatalogStartTab)}
              options={[
                { label: 'Todo', value: 'all' },
                { label: 'Top items', value: 'top' },
              ]}
              value={catalogStartTab}
            />
          </div>
        </div>
        <div className="mt-5 border-t border-[var(--separator)] pt-4">
          <Button disabled={clearingCache} onClick={() => void handleClearCache()} type="button" variant="dangerSoft" fullWidth>
            <Trash2 className="h-4 w-4" />
            {clearingCache ? 'Borrando caché…' : 'Borrar caché local'}
          </Button>
          <p className="mt-2 text-xs text-[var(--muted)]">Borra los datos locales y conserva solo la URL del backend de impresión y el token.</p>
          {clearCacheError ? <p className="mt-2 text-sm text-red-600 dark:text-red-300" role="alert">{clearCacheError}</p> : null}
        </div>
      </section>
    </AppModal>
  )
}
